<template>
  <div class="activity-analysis">
    <!-- 필터 섹션 -->
    <div class="filter-section mb-4">
      <a-row :gutter="16" align="middle" class="filter-row">
        <a-col :span="6">
          <label class="filter-label">
            이벤트
            <span v-if="selectedEventId" style="margin-left: 8px;">
              <a-typography-text
                :copyable="{ text: selectedEventId }"
                style="color: #666; font-size: 12px;"
              >
                (ID: {{ selectedEventId }})
              </a-typography-text>
            </span>
          </label>
          <a-select
            v-model:value="selectedEventId"
            style="width: 100%"
            placeholder="이벤트를 선택하세요"
            @change="handleEventChange"
            showSearch
            :filter-option="filterOption"
            allow-clear
          >
            <a-select-option v-for="event in eventList" :key="event._id" :value="event._id">
              {{ event.eventTitle || event.title || event._id }} ({{ getEventStatusText(event.eventStatus || event.status) }})
            </a-select-option>
          </a-select>
        </a-col>

        <a-col :span="5">
          <label class="filter-label">활동 유형</label>
          <a-select
            v-model:value="filterActivityType"
            style="width: 100%"
            placeholder="전체"
            allow-clear
            @change="handleFilterChange"
          >
            <a-select-option value="">전체</a-select-option>
            <a-select-option v-for="(count, type) in activityTypes" :key="type" :value="type">
              {{ type }} ({{ count }}건)
            </a-select-option>
          </a-select>
        </a-col>

        <a-col :span="7">
          <label class="filter-label">기간 설정</label>
          <a-range-picker
            v-model:value="dateRange"
            style="width: 100%"
            :format="'YYYY-MM-DD'"
            @change="handleDateChange"
          />
        </a-col>

        <a-col :span="6" style="text-align: right;">
          <a-space>
            <a-button type="primary" @click="fetchActivities" :loading="loading" class="yellow-button">
              <template #icon><SearchOutlined /></template>
              조회
            </a-button>
            <a-tooltip title="모든 필터 초기화">
              <a-button @click="resetFilters">
                <template #icon><i class="fa fa-eraser"></i></template>
                필터 초기화
              </a-button>
            </a-tooltip>
            <a-badge :count="activeFilterCount" :number-style="{ backgroundColor: '#52c41a' }">
              <a-button type="text" disabled v-if="activeFilterCount > 0">
                {{ activeFilterCount }}개 필터 적용중
              </a-button>
            </a-badge>
          </a-space>
        </a-col>
      </a-row>
    </div>

    <!-- 요약 정보 카드 -->
    <div class="summary-cards">
      <a-row :gutter="[16, 16]">
        <a-col :span="6">
          <a-card size="small" :hoverable="true">
            <a-statistic
              title="총 활동 수"
              :value="summary?.totalActivities || 0"
              :value-style="{ color: '#3f8600' }"
            >
              <template #prefix>
                <FileTextOutlined />
              </template>
            </a-statistic>
            <div style="margin-top: 8px;">
              <a-button
                size="small"
                type="primary"
                block
                @click="fetchActivities"
                :loading="loading"
                class="yellow-button"
              >
                <template #icon><SearchOutlined /></template>
                조회
              </a-button>
            </div>
          </a-card>
        </a-col>

        <a-col :span="6">
          <a-card size="small" :hoverable="true">
            <a-statistic
              title="고유 참여자 수"
              :value="summary?.uniqueCustomers || 0"
              :value-style="{ color: '#1890ff' }"
            >
              <template #prefix>
                <UserOutlined />
              </template>
            </a-statistic>
            <div style="margin-top: 8px;">
              <a-button
                size="small"
                type="primary"
                block
                @click="fetchUserActivities"
                :loading="userActivitiesLoading"
              >
                <template #icon><UserOutlined /></template>
                사용자별 분석
              </a-button>
            </div>
          </a-card>
        </a-col>

        <a-col :span="12">
          <a-card size="small">
            <a-statistic
              title="데이터 기간"
              :value="summary?.dateRange ? ' ' : '-'"
              :value-style="{ fontSize: '16px', color: '#1890ff', lineHeight: '32px' }"
              :formatter="() => summary?.dateRange ? `${formatDate(summary.dateRange.from)} ~ ${formatDate(summary.dateRange.to)}` : '-'"
            >
              <template #prefix>
                <CalendarOutlined />
              </template>
            </a-statistic>
            <div style="margin-top: 8px;">
              <!-- 투명한 버튼 공간으로 높이 맞추기 -->
              <div style="height: 32px;"></div>
            </div>
          </a-card>
        </a-col>
      </a-row>
    </div>

    <!-- 활동 데이터 테이블 - 기본 모드 -->
    <div class="activity-table" v-if="!showUserMode">
      <a-table
        :columns="columns"
        :data-source="activities"
        :loading="loading"
        :pagination="pagination"
        @change="handleTableChange"
        :scroll="{ x: 1500, y: 600 }"
        size="middle"
        :rowKey="record => record._id"
      >
        <!-- 고객명 컬럼 -->
        <template #customerName="{ record }">
          <span>{{ record.customerName || '-' }}</span>
        </template>

        <!-- 활동 유형 컬럼 -->
        <template #activityType="{ record }">
          <a-tag :color="getActivityTypeColor(record.activityType)">
            {{ record.activityType }}
          </a-tag>
        </template>

        <!-- 타임스탬프 컬럼 -->
        <template #timestamp="{ record }">
          {{ formatDateTime(record.timestamp) }}
        </template>

        <!-- 상세 정보 컬럼 -->
        <template #details="{ record }">
          <a-button type="link" size="small" @click="showDetails(record)">
            상세보기
          </a-button>
        </template>

        <!-- 점수 컬럼 -->
        <template #score="{ record }">
          <span :style="{ color: record.score > 0 ? '#52c41a' : '#666' }">
            {{ record.score || 0 }}점
          </span>
        </template>
      </a-table>
    </div>

    <!-- 사용자별 활동 데이터 테이블 -->
    <div class="activity-table" v-if="showUserMode">
      <div class="table-header">
        <h3>사용자별 활동 분석</h3>
      </div>

      <a-table
        :columns="userColumns"
        :data-source="userActivities"
        :loading="userActivitiesLoading"
        :pagination="userPagination"
        @change="handleUserTableChange"
        :scroll="{ x: 1400 }"
        size="middle"
        :rowKey="record => record.customerId"
      >
        <!-- 고객명 컬럼 -->
        <template #customerName="{ record }">
          <a-space>
            <span>{{ record.customerName || '-' }}</span>
            <a-tag v-if="record.certificate" color="blue" size="small">
              인증서 보유
            </a-tag>
          </a-space>
        </template>

        <!-- 병원명 컬럼 -->
        <template #hospitalName="{ record }">
          {{ record.certificate?.hospitalName || '-' }}
        </template>

        <!-- 총 활동 수 컬럼 -->
        <template #totalActivities="{ record }">
          <span>{{ record.totalActivities }}건</span>
        </template>

        <!-- 활동 기간 컬럼 -->
        <template #duration="{ record }">
          <a-space direction="vertical" size="small" style="font-size: 12px;">
            <span>첫 활동: {{ formatDateTime(record.firstActivity) }}</span>
            <span>마지막: {{ formatDateTime(record.lastActivity) }}</span>
            <span>
              <ClockCircleOutlined />
              {{ formatDuration(record.activityDuration) }}
            </span>
          </a-space>
        </template>

        <!-- 상세보기 컬럼 -->
        <template #recentActivities="{ record }">
          <a-button type="link" size="small" @click="showUserDetails(record)">
            상세보기
          </a-button>
        </template>
      </a-table>
    </div>

    <!-- 사용자 활동 상세 드로워 -->
    <a-drawer
      v-model:visible="userDetailsDrawerVisible"
      :title="`${selectedUser?.customerName || '사용자'}의 활동 상세`"
      :width="900"
      placement="right"
    >
      <div v-if="selectedUser">
        <!-- 사용자 기본 정보 -->
        <a-descriptions :column="2" bordered size="small" style="margin-bottom: 20px;">
          <a-descriptions-item label="고객명">{{ selectedUser.customerName }}</a-descriptions-item>
          <a-descriptions-item label="고객 ID">{{ selectedUser.customerId }}</a-descriptions-item>
          <a-descriptions-item label="총 활동 수">{{ selectedUser.totalActivities }}건</a-descriptions-item>
          <a-descriptions-item label="활동 시간">{{ formatDuration(selectedUser.activityDuration) }}</a-descriptions-item>
          <a-descriptions-item label="첫 활동" :span="2">
            {{ formatDateTime(selectedUser.firstActivity) }}
          </a-descriptions-item>
          <a-descriptions-item label="마지막 활동" :span="2">
            {{ formatDateTime(selectedUser.lastActivity) }}
          </a-descriptions-item>
        </a-descriptions>

        <!-- 인증서 정보 -->
        <div v-if="selectedUser.certificate" style="margin-bottom: 20px;">
          <a-divider>인증서 정보</a-divider>
          <a-descriptions :column="1" bordered size="small">
            <a-descriptions-item label="병원명">{{ selectedUser.certificate.hospitalName }}</a-descriptions-item>
            <a-descriptions-item label="수술일">{{ formatDate(selectedUser.certificate.surgeryDate) }}</a-descriptions-item>
            <a-descriptions-item label="수술명">{{ selectedUser.certificate.surgeryName }}</a-descriptions-item>
          </a-descriptions>
        </div>

        <!-- 전체 활동 목록 -->
        <a-divider>
          전체 활동 내역
          <a-button
            size="small"
            type="primary"
            style="margin-left: 10px;"
            @click="fetchUserAllActivities"
            :loading="userAllActivitiesLoading"
          >
            <template #icon><ReloadOutlined /></template>
            전체 조회
          </a-button>
        </a-divider>

        <!-- 활동 테이블 -->
        <div v-if="userAllActivities.length > 0">
          <a-table
            :columns="userActivityColumns"
            :data-source="userAllActivities"
            :loading="userAllActivitiesLoading"
            :pagination="userActivityPagination"
            @change="handleUserActivityTableChange"
            size="small"
            :scroll="{ y: 400 }"
            :rowKey="(record, index) => index"
          >
            <template #timestamp="{ record }">
              {{ formatDateTime(record.timestamp) }}
            </template>
            <template #activityType="{ record }">
              <a-tag size="small">{{ record.activityType || '-' }}</a-tag>
            </template>
            <template #details="{ record }">
              <div style="font-size: 12px;">
                <div>{{ record.details?.stepName || record.details?.action || '-' }}</div>
                <div style="color: #666;">{{ record.details?.pageName || '-' }}</div>
              </div>
            </template>
            <template #score="{ record }">
              <span v-if="record.score" style="color: #52c41a;">
                +{{ record.score }}점
              </span>
              <span v-else>-</span>
            </template>
          </a-table>
        </div>

        <!-- 최근 활동 타임라인 (초기 표시) -->
        <div v-else-if="selectedUser.recentActivities">
          <a-timeline>
            <a-timeline-item v-for="(activity, idx) in selectedUser.recentActivities" :key="idx">
              <template #dot>
                <FileTextOutlined style="font-size: 16px;" />
              </template>
              <div>
                <div style="margin-bottom: 4px;">
                  <strong>{{ formatDateTime(activity.timestamp) }}</strong>
                </div>
                <div style="margin-bottom: 4px;">
                  <a-tag>{{ activity.details?.stepName || activity.details?.action || '-' }}</a-tag>
                  <span v-if="activity.score" style="margin-left: 8px; color: #52c41a;">
                    +{{ activity.score }}점
                  </span>
                </div>
                <div style="font-size: 12px; color: #666;">
                  {{ activity.details?.pageName || '-' }}
                </div>
              </div>
            </a-timeline-item>
          </a-timeline>
        </div>
      </div>

      <template #footer>
        <a-button @click="closeUserDetails">닫기</a-button>
      </template>
    </a-drawer>

    <!-- 상세 정보 드로워 -->
    <a-drawer
      v-model:visible="detailsDrawerVisible"
      title="활동 상세 정보"
      :width="720"
      placement="right"
      :bodyStyle="{ paddingBottom: '80px' }"
    >
      <div v-if="selectedActivity">
        <a-descriptions :column="2" bordered size="small">
          <a-descriptions-item label="활동 ID" :span="2">
            {{ selectedActivity._id }}
          </a-descriptions-item>
          <a-descriptions-item label="고객 ID">
            {{ selectedActivity.customerId }}
          </a-descriptions-item>
          <a-descriptions-item label="고객명">
            {{ selectedActivity.customerName || '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="활동 유형">
            <a-tag :color="getActivityTypeColor(selectedActivity.activityType)">
              {{ selectedActivity.activityType }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="타임스탬프">
            {{ formatDateTime(selectedActivity.timestamp) }}
          </a-descriptions-item>
          <a-descriptions-item label="점수">
            <span :style="{ color: selectedActivity.score > 0 ? '#52c41a' : '#666' }">
              {{ selectedActivity.score || 0 }}점
            </span>
          </a-descriptions-item>
          <a-descriptions-item label="점수 유형">
            {{ selectedActivity.scoreType || '-' }}
          </a-descriptions-item>
        </a-descriptions>

        <a-divider>상세 데이터</a-divider>

        <div class="json-viewer">
          <pre>{{ JSON.stringify(selectedActivity.details, null, 2) }}</pre>
        </div>

        <div v-if="selectedActivity.certificate" style="margin-top: 16px">
          <a-divider>인증서 정보</a-divider>
          <a-descriptions :column="1" bordered size="small">
            <a-descriptions-item label="병원명">
              {{ selectedActivity.certificate.hospitalName || '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="수술일">
              {{ formatDate(selectedActivity.certificate.surgeryDate) || '-' }}
            </a-descriptions-item>
          </a-descriptions>
        </div>
      </div>

      <template #footer>
        <a-button @click="detailsDrawerVisible = false">닫기</a-button>
      </template>
    </a-drawer>
  </div>
</template>

<script>
import { ref, reactive, onMounted, computed } from 'vue';
import { message } from 'ant-design-vue';
import { useStore } from 'vuex';
import {
  SearchOutlined,
  ReloadOutlined,
  FileTextOutlined,
  UserOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import eventsApis from '../../api';
import axios from '@/axios';

export default {
  name: 'ActivityAnalysis',
  components: {
    SearchOutlined,
    ReloadOutlined,
    FileTextOutlined,
    UserOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    },
  props: {
    eventId: {
      type: String,
      default: ''
    }
  },
  setup(props) {
    const store = useStore();
    const apis = eventsApis(axios, store);

    // 상태 관리
    const selectedEventId = ref(props.eventId || '68ca33f040d408b2a0a30924'); // 기본 이벤트 ID
    const filterActivityType = ref('');
    const dateRange = ref(null);
    const loading = ref(false);
    const activities = ref([]);
    const summary = ref(null);
    const activityTypes = ref({});
    const eventList = ref([]);
    const detailsDrawerVisible = ref(false);
    const selectedActivity = ref(null);

    // 사용자별 활동 데이터 관련 상태
    const showUserMode = ref(false);
    const userActivities = ref([]);
    const userActivitiesLoading = ref(false);
    const userDetailsDrawerVisible = ref(false);
    const selectedUser = ref(null);
    const userAllActivities = ref([]);
    const userAllActivitiesLoading = ref(false);

    // 활성 필터 개수 계산
    const activeFilterCount = computed(() => {
      let count = 0;
      if (filterActivityType.value) count++;
      if (dateRange.value && dateRange.value.length === 2) count++;
      return count;
    });

    // 페이지네이션
    const pagination = reactive({
      current: 1,
      pageSize: 50,
      total: 0,
      showSizeChanger: true,
      showTotal: (total, range) => `${range[0]}-${range[1]} / 총 ${total}개`,
      pageSizeOptions: ['20', '50', '100', '200']
    });

    // 사용자별 페이지네이션
    const userPagination = reactive({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      showTotal: (total, range) => `${range[0]}-${range[1]} / 총 ${total}명`,
      pageSizeOptions: ['10', '20', '50', '100']
    });

    // 사용자 활동 상세 페이지네이션
    const userActivityPagination = reactive({
      current: 1,
      pageSize: 50,
      total: 0,
      showSizeChanger: true,
      showTotal: (total) => `총 ${total}개 활동`,
      pageSizeOptions: ['20', '50', '100', '200']
    });

    // 테이블 컬럼 정의
    const columns = [
      {
        title: '시간',
        dataIndex: 'timestamp',
        key: 'timestamp',
        width: 180,
        sorter: true,
        customRender: ({ record }) => formatDateTime(record.timestamp)
      },
      {
        title: '고객명',
        dataIndex: 'customerName',
        key: 'customerName',
        width: 100,
        ellipsis: true
      },
      {
        title: '활동 유형',
        dataIndex: 'activityType',
        key: 'activityType',
        width: 200,
        slots: { customRender: 'activityType' }
      },
      {
        title: '페이지',
        key: 'pageName',
        width: 150,
        customRender: ({ record }) => record.details?.pageName || '-'
      },
      {
        title: '액션',
        key: 'action',
        width: 150,
        customRender: ({ record }) => record.details?.action || record.details?.step || '-'
      },
      {
        title: '버튼/위치',
        key: 'button',
        width: 150,
        ellipsis: true,
        customRender: ({ record }) => record.details?.btnName || record.details?.position || '-'
      },
      {
        title: '점수',
        dataIndex: 'score',
        key: 'score',
        width: 80,
        align: 'center',
        slots: { customRender: 'score' }
      },
      {
        title: '상세',
        key: 'details',
        width: 100,
        fixed: 'right',
        align: 'center',
        slots: { customRender: 'details' }
      }
    ];

    // 사용자별 테이블 컬럼 정의
    const userColumns = [
      {
        title: '고객명',
        dataIndex: 'customerName',
        key: 'customerName',
        width: 150,
        slots: { customRender: 'customerName' }
      },
      {
        title: '병원',
        key: 'hospitalName',
        width: 150,
        slots: { customRender: 'hospitalName' }
      },
      {
        title: '총 활동 수',
        dataIndex: 'totalActivities',
        key: 'totalActivities',
        width: 120,
        sorter: true,
        slots: { customRender: 'totalActivities' }
      },
      {
        title: '활동 기간',
        key: 'duration',
        width: 200,
        slots: { customRender: 'duration' }
      },
      {
        title: '상세',
        key: 'recentActivities',
        width: 100,
        align: 'center',
        slots: { customRender: 'recentActivities' }
      }
    ];

    // 사용자 활동 상세 테이블 컬럼
    const userActivityColumns = [
      {
        title: '시간',
        dataIndex: 'timestamp',
        key: 'timestamp',
        width: 150,
        slots: { customRender: 'timestamp' }
      },
      {
        title: '활동 유형',
        dataIndex: 'activityType',
        key: 'activityType',
        width: 150,
        slots: { customRender: 'activityType' }
      },
      {
        title: '상세 내용',
        key: 'details',
        width: 250,
        slots: { customRender: 'details' }
      },
      {
        title: '점수',
        dataIndex: 'score',
        key: 'score',
        width: 80,
        align: 'center',
        slots: { customRender: 'score' }
      }
    ];

    // 메서드
    const fetchEventList = async () => {
      // getEventsWithStatus를 사용하여 이벤트 목록 가져오기 (참여현황 모니터링과 동일)
      apis.getEventsWithStatus(
        { status: 'ongoing', limit: 100 },
        (response) => {
          if (response.data) {
            const events = response.data.items || response.data.data || response.data || [];
            eventList.value = Array.isArray(events) ? events : [];
            console.log('이벤트 목록 로드 완료:', eventList.value.length, '개');

            // 써마지쿠폰수령 고정 옵션 추가
            const thermageOption = {
              _id: 'THERMAGE',
              eventTitle: '써마지쿠폰수령',
              eventStatus: 'ongoing'
            };
            eventList.value.push(thermageOption);
          }
        },
        (error) => {
          console.error('이벤트 목록 조회 실패:', error);
        }
      );
    };

    const fetchActivities = async () => {
      if (!selectedEventId.value) {
        message.warning('이벤트를 선택해주세요.');
        return;
      }

      // 사용자별 분석 모드 종료
      showUserMode.value = false;

      loading.value = true;

      const params = {
        page: pagination.current,
        limit: pagination.pageSize
      };

      // 필터 적용
      if (filterActivityType.value) {
        params.activityType = filterActivityType.value;
      }

      if (dateRange.value && dateRange.value.length === 2) {
        params.startDate = dateRange.value[0].startOf('day').toISOString();
        params.endDate = dateRange.value[1].endOf('day').toISOString();
      }

      apis.getAttendeeActivities(selectedEventId.value, params,
        (response) => {
          if (response.data?.success) {
            const data = response.data.data;
            activities.value = data.activities || [];
            summary.value = data.summary || null;

            // 활동 유형 업데이트
            if (data.summary?.activityTypes) {
              activityTypes.value = data.summary.activityTypes;
            }

            // 페이지네이션 업데이트
            if (data.pagination) {
              pagination.current = data.pagination.page;
              pagination.pageSize = data.pagination.limit;
              pagination.total = data.pagination.total;
            }
          }
        },
        (error) => {
          console.error('활동 데이터 조회 실패:', error);
          message.error('활동 데이터 조회에 실패했습니다.');
        }
      ).finally(() => {
        loading.value = false;
      });
    };

    const handleEventChange = (value) => {
      selectedEventId.value = value;
      pagination.current = 1;
      fetchActivities();
    };

    const handleFilterChange = () => {
      pagination.current = 1;
      fetchActivities();
    };

    const handleDateChange = () => {
      pagination.current = 1;
      fetchActivities();
    };

    const handleTableChange = (pag, filters, sorter) => {
      pagination.current = pag.current;
      pagination.pageSize = pag.pageSize;
      fetchActivities();
    };

    const resetFilters = () => {
      filterActivityType.value = '';
      dateRange.value = null;
      pagination.current = 1;
      fetchActivities();
    };

    const showDetails = (record) => {
      selectedActivity.value = record;
      detailsDrawerVisible.value = true;
    };

    // 사용자별 활동 데이터 조회
    const fetchUserActivities = async () => {
      if (!selectedEventId.value) {
        message.warning('이벤트를 선택해주세요.');
        return;
      }

      userActivitiesLoading.value = true;
      showUserMode.value = true;

      const params = {
        page: userPagination.current,
        limit: userPagination.pageSize,
        sortBy: 'totalActivities',
        sortOrder: 'desc'
      };

      apis.getAttendeeActivitiesByUser(selectedEventId.value, params,
        (response) => {
          if (response.data?.success) {
            const data = response.data.data;
            userActivities.value = data.users || [];

            // 페이지네이션 업데이트
            if (data.pagination) {
              userPagination.current = data.pagination.page;
              userPagination.pageSize = data.pagination.limit;
              userPagination.total = data.pagination.total;
            }
          }
        },
        (error) => {
          console.error('사용자별 활동 데이터 조회 실패:', error);
          message.error('사용자별 활동 데이터 조회에 실패했습니다.');
          showUserMode.value = false;
        }
      ).finally(() => {
        userActivitiesLoading.value = false;
      });
    };

    const showUserDetails = (user) => {
      selectedUser.value = user;
      userAllActivities.value = [];  // 초기화
      userDetailsDrawerVisible.value = true;
    };

    // 사용자의 전체 활동 데이터 조회
    const fetchUserAllActivities = async () => {
      if (!selectedEventId.value || !selectedUser.value) {
        return;
      }

      userAllActivitiesLoading.value = true;

      const params = {
        page: userActivityPagination.current,
        limit: userActivityPagination.pageSize,
        customerId: selectedUser.value.customerId
      };

      apis.getAttendeeActivities(selectedEventId.value, params,
        (response) => {
          if (response.data?.success) {
            const data = response.data.data;
            userAllActivities.value = data.activities || [];

            // 페이지네이션 업데이트
            if (data.pagination) {
              userActivityPagination.current = data.pagination.page;
              userActivityPagination.pageSize = data.pagination.limit;
              userActivityPagination.total = data.pagination.total;
            }
          }
        },
        (error) => {
          console.error('사용자 활동 데이터 조회 실패:', error);
          message.error('활동 데이터 조회에 실패했습니다.');
        }
      ).finally(() => {
        userAllActivitiesLoading.value = false;
      });
    };

    const handleUserActivityTableChange = (pag) => {
      userActivityPagination.current = pag.current;
      userActivityPagination.pageSize = pag.pageSize;
      fetchUserAllActivities();
    };

    const closeUserDetails = () => {
      userDetailsDrawerVisible.value = false;
      userAllActivities.value = [];  // 드로워 닫을 때 초기화
    };

    const handleUserTableChange = (pag, filters, sorter) => {
      userPagination.current = pag.current;
      userPagination.pageSize = pag.pageSize;
      fetchUserActivities();
    };

    const formatDuration = (minutes) => {
      if (!minutes) return '-';
      const hours = Math.floor(minutes / 60);
      const mins = Math.floor(minutes % 60);
      if (hours > 0) {
        return `${hours}시간 ${mins}분`;
      }
      return `${mins}분`;
    };

    const filterOption = (input, option) => {
      return option.children[0].children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    };

    // 이벤트 상태 텍스트 반환
    const getEventStatusText = (status) => {
      const statusMap = {
        'preparing': '준비중',
        'ongoing': '진행중',
        'ended': '종료',
        'active': '진행중',
        'inactive': '비활성'
      };
      return statusMap[status] || status || '알수없음';
    };

    // 유틸리티 함수
    const formatDate = (date) => {
      return date ? dayjs(date).format('YYYY-MM-DD') : '-';
    };

    const formatDateTime = (date) => {
      return date ? dayjs(date).format('YYYY-MM-DD HH:mm:ss') : '-';
    };

    const getActivityTypeColor = (type) => {
      const colorMap = {
        '카카오톡 공유하기': 'green',
        '카카오톡 인증서 수신 페이지': 'blue',
        'landing': 'purple',
        'share': 'cyan',
        '올리브영 상품권 도전하기': 'orange',
        '응모하기': 'red',
        'quiz': 'magenta',
        'message_sent': 'lime'
      };
      return colorMap[type] || 'default';
    };

    // 생명주기
    onMounted(() => {
      fetchEventList();
      // 탭 진입 시 자동으로 활동 데이터 조회
      if (selectedEventId.value) {
        fetchActivities();
      } else {
        // 이벤트 목록 로드 후 첫 번째 이벤트로 자동 조회
        setTimeout(() => {
          if (eventList.value.length > 0 && !selectedEventId.value) {
            selectedEventId.value = eventList.value[0]._id;
            fetchActivities();
          }
        }, 500);
      }
    });

    return {
      selectedEventId,
      filterActivityType,
      dateRange,
      loading,
      activities,
      summary,
      activityTypes,
      eventList,
      columns,
      pagination,
      detailsDrawerVisible,
      selectedActivity,
      activeFilterCount,
      fetchActivities,
      handleEventChange,
      handleFilterChange,
      handleDateChange,
      handleTableChange,
      resetFilters,
      showDetails,
      filterOption,
      formatDate,
      formatDateTime,
      formatDuration,
      getActivityTypeColor,
      getEventStatusText,
      // 사용자별 활동 데이터 관련
      showUserMode,
      userActivities,
      userActivitiesLoading,
      userColumns,
      userPagination,
      userDetailsDrawerVisible,
      selectedUser,
      userAllActivities,
      userAllActivitiesLoading,
      userActivityColumns,
      userActivityPagination,
      fetchUserActivities,
      showUserDetails,
      fetchUserAllActivities,
      handleUserActivityTableChange,
      closeUserDetails,
      handleUserTableChange
    };
  }
};
</script>

<style scoped>
.activity-analysis {
  padding: 0;
}

.filter-section {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.filter-row {
  margin-bottom: 0;
}

.filter-label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #333;
  font-size: 13px;
}

.help-icon {
  color: #999;
  font-size: 12px;
  margin-left: 4px;
  cursor: pointer;
}

.help-icon:hover {
  color: #1890ff;
}

.mb-4 {
  margin-bottom: 1.5rem !important;
}

.mt-3 {
  margin-top: 1rem !important;
}

.summary-cards {
  margin-bottom: 20px;
}

.activity-table {
  background: #fff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.json-viewer {
  background: #f5f5f5;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  padding: 12px;
  max-height: 400px;
  overflow-y: auto;
}

.json-viewer pre {
  margin: 0;
  font-size: 12px;
  font-family: 'Consolas', 'Monaco', monospace;
  white-space: pre-wrap;
  word-wrap: break-word;
}

:deep(.ant-table) {
  font-size: 13px;
}

:deep(.ant-tag) {
  font-size: 12px;
}

:deep(.ant-card-body) {
  padding: 16px;
}

:deep(.ant-statistic-title) {
  font-size: 13px;
  color: #666;
}

:deep(.ant-statistic-content) {
  font-size: 20px;
}

.yellow-button {
  background-color: #faad14 !important;
  border-color: #faad14 !important;
}

.yellow-button:hover {
  background-color: #ffc53d !important;
  border-color: #ffc53d !important;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.table-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}
</style>